import { MapLayer, MapLayerType } from '@app/redux/riskMap/riskMapSlice'
import { PUBLIC_ASSETS_BASE_URL } from '@app/app-constants'
import {
  DialogData,
  HazardDetail,
} from '@app/components/Molecules/MapView/RiskMapView/AddLayersDialog'
import { BASE_DOMAIN } from '@app/utils/redirectToBaseDomain'
import { slugify, snakeCaseToTitleCase } from '@app/utils/strings.utils'
import { Branding, SequencedStep, Story, StoryMapState, StoryMedia } from '../types'

// Index layers data
export const getIndexedLayers = (layerDialog: DialogData) => {
  // Collect scenarios by their assetTags
  const scenariosData: { [key: string]: MapLayer } = {}
  layerDialog.hazardDialogData.hazardDetails.forEach((hazard: HazardDetail) => {
    hazard.scenarios.forEach((scenario) => {
      scenariosData[scenario.assetTag] = scenario
    })
  })

  // Collect elements by their ids
  const elementsData: { [key: string]: MapLayer } = {}
  Object.values(layerDialog.assetDialogData).forEach((domain) => {
    domain.groups.forEach((group) => {
      group.assets.forEach((element) => {
        elementsData[element.type] = element
      })
    })
  })

  // Collect contexts by their ids
  const contextsData: { [key: string]: MapLayer } = {}
  Object.values(layerDialog.informationDialogData).forEach((menu) => {
    menu.groups.forEach((group) => {
      group.layers.forEach((layer) => {
        contextsData[layer.id] = layer
      })
    })
  })
  return { elementsData, scenariosData, contextsData }
}

const getStoryOverviewSequencedStep = (story: Story): SequencedStep => {
  return {
    stepTitle: story.title,
    sections: [
      {
        content: story.description,
      },
    ],
    location: story.location,
    sequencedIndex: 0,
    basemap: story.basemap,
  }
}

const getInheritedProperty = function <T>(
  property: keyof StoryMapState,
  stepIndex: number,
  chapterIndex: number,
  story: Story,
): T | undefined {
  if (story.chapters[chapterIndex].steps[stepIndex][property]) {
    return story.chapters[chapterIndex].steps[stepIndex][property] as T
  }
  if (story.chapters[chapterIndex][property]) {
    return story.chapters[chapterIndex][property] as T
  }
  if (story[property]) {
    return story[property] as T
  }
  return undefined
}

export const generateSequencedSteps = (
  story: Story,
  elementsData: {
    [key: string]: MapLayer
  },
  scenariosData: {
    [key: string]: MapLayer
  },
  contextsData: {
    [key: string]: MapLayer
  },
): SequencedStep[] => {
  const steps: SequencedStep[] = [getStoryOverviewSequencedStep(story)]
  for (let i = 0; i < story.chapters.length; i++) {
    for (let j = 0; j < story.chapters[i].steps.length; j++) {
      const newStep: SequencedStep = {
        sequencedIndex: steps.length,
        chapterTitle: j == 0 ? story.chapters[i].title : undefined,
        stepTitle: story.chapters[i].steps[j].title,
        sections: story.chapters[i].steps[j].sections,
        location: getInheritedProperty('location', j, i, story),
        elements: getInheritedProperty<string[]>('elements', j, i, story),
        scenarios: getInheritedProperty<string[]>('scenarios', j, i, story),
        contexts: getInheritedProperty<string[]>('contexts', j, i, story),
        basemap: getInheritedProperty<string>('basemap', j, i, story) || 'Street',
        regions: getInheritedProperty<string[]>('regions', j, i, story),
        media: getInheritedProperty<StoryMedia[]>('media', j, i, story),
      }
      if (newStep.elements && newStep.elements.length > 0) {
        newStep.elementsText = newStep.elements
          .flatMap((elementId) => {
            if (elementsData[elementId]) return [elementsData[elementId].display_name]
            return []
          })
          .join(', ')
      }

      if (newStep.scenarios?.length) {
        newStep.scenariosText = newStep.scenarios
          .flatMap((scenarioId) => {
            if (scenariosData[scenarioId]) return [scenariosData[scenarioId].display_name]
            return []
          })
          .join(', ')
      }

      if (newStep.contexts?.length) {
        newStep.contextsText = newStep.contexts
          .flatMap((contextId) => {
            if (contextsData[contextId]) return [contextsData[contextId].display_name]
            return []
          })
          .join(', ')
      }

      steps.push(newStep)
    }
  }
  return steps
}
export const getCurrentElements = (elementsData: {
  [key: string]: MapLayer
}): { [key: string]: MapLayer } => {
  return (
    Object.entries(elementsData)?.reduce(
      (acc, [layerId, mapLayer]) => {
        acc[layerId] = {
          ...mapLayer,
          visible: true,
          interactivityDisabled: false,
          layerType: 'asset' as MapLayerType, // or hazard or information
          id: mapLayer.tilesets?.[0]?.id,
        }
        return acc
      },
      {} as { [key: string]: MapLayer },
    ) ?? {}
  )
}

export const getCurrentHazards = (scenariosData: {
  [key: string]: MapLayer
}): { [key: string]: MapLayer } => {
  return (
    Object.entries(scenariosData)?.reduce(
      (acc, [layerId, mapLayer]) => {
        acc[layerId] = {
          ...mapLayer,
          display_name: mapLayer.display_name
            ? mapLayer.display_name
            : snakeCaseToTitleCase(mapLayer.assetTag),
          legend: mapLayer.legend,
          visible: true,
          interactivityDisabled: false,
          icon: mapLayer.icon ?? 'Warning',
          layerType: 'hazard',
          id: mapLayer.tilesets?.[0]?.id,
        }
        return acc
      },
      {} as { [key: string]: MapLayer },
    ) ?? []
  )
}
export const getCurrentContexts = (contextsData: {
  [key: string]: MapLayer
}): { [key: string]: MapLayer } => {
  return (
    Object.entries(contextsData)?.reduce(
      (acc, [layerId, mapLayer]) => {
        acc[layerId] = {
          ...mapLayer,
          visible: true,
          interactivityDisabled: false,
          layerType: 'information',
          id: mapLayer.tilesets?.[0]?.id,
        }
        return acc
      },
      {} as { [key: string]: MapLayer },
    ) ?? {}
  )
}

export const addCloudfrontUrl = (url: string | undefined) => {
  if (!url) return undefined
  if (!url.includes(PUBLIC_ASSETS_BASE_URL)) {
    const newUrl = new URL(('public/' + url).replaceAll('//', '/'), PUBLIC_ASSETS_BASE_URL)
    return newUrl.toString()
  }
  return url
}

export const primaryDefault = '#0B2948'
export const primaryContrastDefault = '#FFFFFF'
export const secondaryDefault = '#0074D3'
export const secondaryContrastDefault = '#FFFFFF'

export const defaultBranding: Branding = {
  primary: primaryDefault,
  primaryContrast: primaryContrastDefault,
  secondary: secondaryDefault,
  secondaryContrast: secondaryContrastDefault,
}

export const defaultRiskStoryCoverImage = 'images/RiskStoriesDefaultCoverImage.jpg'

export const generateStoryLink = (story: Story | null, clientName: string | null): string => {
  if (!story || !clientName) return ''

  const slug = slugify(story.title)
  return `https://${clientName}.${BASE_DOMAIN}/risk/stories/${story.id}${
    slug ? `/${slug}` : '/risk-story'
  }/view`
}

export const generateBrandedMediaPinSvg = (branding: Branding): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="44" height="53" viewBox="0 0 44 53" fill="none">  <path d="M22 0.5C33.8741 0.5 43.5 10.1259 43.5 22C43.5 27.9941 39.661 33.9791 35.1631 39.166C30.6628 44.3556 25.3576 48.8982 22.3535 51.9023L22 52.2559L21.6465 51.9023C18.3792 48.6351 13.0744 44.0929 8.63965 38.9688C4.20449 33.8441 0.5 27.9916 0.5 22C0.500001 10.1259 10.1259 0.5 22 0.5Z" fill="${branding.secondaryContrast}" stroke="${branding.secondaryContrast}"/>  
          <g transform="translate(6, 6)">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">  <rect x="0.5" y="0.5" width="31" height="31" stroke="${branding.secondaryContrast}"/>  <path d="M16 22.875C17.5625 22.875 18.8906 22.3281 19.9844 21.2344C21.0781 20.1406 21.625 18.8125 21.625 17.25C21.625 15.6875 21.0781 14.3594 19.9844 13.2656C18.8906 12.1719 17.5625 11.625 16 11.625C14.4375 11.625 13.1094 12.1719 12.0156 13.2656C10.9219 14.3594 10.375 15.6875 10.375 17.25C10.375 18.8125 10.9219 20.1406 12.0156 21.2344C13.1094 22.3281 14.4375 22.875 16 22.875ZM16 20.375C15.125 20.375 14.3854 20.0729 13.7812 19.4688C13.1771 18.8646 12.875 18.125 12.875 17.25C12.875 16.375 13.1771 15.6354 13.7812 15.0312C14.3854 14.4271 15.125 14.125 16 14.125C16.875 14.125 17.6146 14.4271 18.2188 15.0312C18.8229 15.6354 19.125 16.375 19.125 17.25C19.125 18.125 18.8229 18.8646 18.2188 19.4688C17.6146 20.0729 16.875 20.375 16 20.375ZM6 27.25C5.3125 27.25 4.72396 27.0052 4.23438 26.5156C3.74479 26.026 3.5 25.4375 3.5 24.75V9.75C3.5 9.0625 3.74479 8.47396 4.23438 7.98438C4.72396 7.49479 5.3125 7.25 6 7.25H9.9375L11.5 5.5625C11.7292 5.3125 12.0052 5.11458 12.3281 4.96875C12.651 4.82292 12.9896 4.75 13.3438 4.75H18.6562C19.0104 4.75 19.349 4.82292 19.6719 4.96875C19.9948 5.11458 20.2708 5.3125 20.5 5.5625L22.0625 7.25H26C26.6875 7.25 27.276 7.49479 27.7656 7.98438C28.2552 8.47396 28.5 9.0625 28.5 9.75V24.75C28.5 25.4375 28.2552 26.026 27.7656 26.5156C27.276 27.0052 26.6875 27.25 26 27.25H6Z" fill="${branding.secondary}"/></svg>
          </g>
        </svg>`
}
